import React from 'react';
import {
  CustomFieldExtensionSchema,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';
import { useApi, identityApiRef, configApiRef } from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';
import { isoToLocalInput, localInputToIso } from '../LocalDateTimePicker';
import {
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from '@material-ui/core';

interface ScopeItem {
  id: string;
  name: string;
  slackDestinations?: string[];
  githubOrgs?: string[];
}

/**
 * Read-only summary shown in the "update" flow of the Publish Notification
 * template. It reads the currently-selected announcement (updateScope) from the
 * live form state, looks up that announcement's saved recipe, and shows where
 * the update will be delivered (the original Slack channels + GitHub orgs).
 *
 * When — and only when — the original announcement set GitHub org banners, it
 * also renders an optional "new banner expiry" input. The field's value is that
 * optional expiry override (an empty string keeps the original expiry).
 */
export function UpdateAnnouncementDetails({
  onChange,
  formData,
  formContext,
  uiSchema,
  schema: { title },
}: FieldExtensionComponentProps<string>): JSX.Element | null {
  const endpoint =
    (uiSchema?.['ui:options']?.scopesEndpoint as string) ||
    'api/notification-blasts/scopes';
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const backendUrl = configApi.getString('backend.baseUrl');

  const selectedScope = (formContext as { formData?: { updateScope?: string } })
    ?.formData?.updateScope;

  const { value: scopes, loading, error } = useAsync(async (): Promise<ScopeItem[]> => {
    const token = (await identityApi.getCredentials()).token;
    const response = await fetch(`${backendUrl}/${endpoint}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      throw new Error(`API error: ${response.statusText}`);
    }
    return response.json();
  }, [backendUrl, endpoint, identityApi]);

  if (!selectedScope) {
    return null;
  }
  if (loading) {
    return <CircularProgress size={20} />;
  }
  if (error) {
    return (
      <Typography color="error">
        Error loading announcement details: {error.message}
      </Typography>
    );
  }

  const scope = (scopes || []).find(s => s.id === selectedScope);
  if (!scope) {
    return null;
  }

  const slack = scope.slackDestinations ?? [];
  const orgs = scope.githubOrgs ?? [];
  const hasGithubBanner = orgs.length > 0;

  return (
    <Box mt={1} mb={1}>
      <Typography variant="subtitle2" gutterBottom>
        {title || 'Where this update will be sent'}
      </Typography>
      {slack.length === 0 && orgs.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          This announcement was not originally sent to any Slack channels or GitHub org banners.
        </Typography>
      ) : (
        <>
          {slack.length > 0 && (
            <Box mb={1}>
              <Typography variant="body2" color="textSecondary">
                Slack channels
              </Typography>
              {slack.map(c => (
                <Chip
                  key={c}
                  size="small"
                  label={c}
                  style={{ marginRight: 4, marginTop: 4 }}
                />
              ))}
            </Box>
          )}
          {orgs.length > 0 && (
            <Box mb={1}>
              <Typography variant="body2" color="textSecondary">
                GitHub org banners
              </Typography>
              {orgs.map(o => (
                <Chip
                  key={o}
                  size="small"
                  label={o}
                  style={{ marginRight: 4, marginTop: 4 }}
                />
              ))}
            </Box>
          )}
        </>
      )}
      {hasGithubBanner && (
        <TextField
          label="New Banner Expiry (optional)"
          type="datetime-local"
          value={isoToLocalInput(formData)}
          onChange={e => onChange(localInputToIso(e.target.value))}
          helperText="Leave blank to keep the original expiry. The update always force-replaces the existing banner."
          InputLabelProps={{ shrink: true }}
          fullWidth
          margin="normal"
        />
      )}
    </Box>
  );
}

export const UpdateAnnouncementDetailsSchema: CustomFieldExtensionSchema = {
  uiOptions: {
    type: 'object',
    properties: {
      scopesEndpoint: {
        type: 'string',
        title: 'Scopes Endpoint',
        description: 'API path returning the announcement scopes with destinations.',
      },
    },
  },
  returnValue: {
    type: 'string',
    title: 'Optional new banner expiry',
  },
};
